import { useState } from 'react';
import { Button, Input, Label, SectionCard } from '@/components/ui';
import { RadioGroup } from '@/components/ui';
import { cn } from '@/components/ui';
import type { CouponCreateRequest, CouponDiscountType, CouponType } from '@/lib/api-admin';
import { ProductMultiSelectModalField } from '@/features/catalog/components/ProductMultiSelectModalField';
import { useCouponCreateMutation } from '../hooks/useCouponMutation';
import { CouponFormSection as FormSection } from './CouponFormSection';

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'FIXED', label: '정액 (원)' },
  { value: 'PERCENT', label: '정률 (%)' },
] as const;

const COUPON_TYPE_OPTIONS = [
  { value: 'CART', label: '장바구니 쿠폰', hint: '주문 전체에 적용' },
  { value: 'PRODUCT', label: '상품 쿠폰', hint: '선택한 상품에만 적용' },
] as const;

interface CouponTypeRadioProps {
  value: CouponType;
  onChange: (value: CouponType) => void;
}

const CouponTypeRadio = ({ value, onChange }: CouponTypeRadioProps) => (
  <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="쿠폰 종류">
    {COUPON_TYPE_OPTIONS.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(opt.value)}
          data-testid={`coupon-type-${opt.value}`}
          className={cn(
            'flex flex-col items-start gap-0.5 rounded-lg border px-4 py-3 text-left transition-colors',
            selected
              ? 'border-[color:var(--accent-default)] bg-[color:var(--surface-1)]'
              : 'border-[color:var(--divider-strong)] bg-[color:var(--surface-0)] hover:bg-[color:var(--surface-1)]',
          )}
        >
          <span
            className="text-sm font-medium"
            style={{ color: selected ? 'var(--accent-default)' : 'var(--content-strong)' }}
          >
            {opt.label}
          </span>
          <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
            {opt.hint}
          </span>
        </button>
      );
    })}
  </div>
);

interface Props {
  onSuccess?: () => void;
}

export const CouponCreateForm = ({ onSuccess }: Props) => {
  const mutation = useCouponCreateMutation();

  const [code, setCode] = useState('');
  const [badge, setBadge] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [couponType, setCouponType] = useState<CouponType>('CART');
  const [discountType, setDiscountType] = useState<CouponDiscountType>('FIXED');
  const [discountAmount, setDiscountAmount] = useState('');
  const [conditionAmount, setConditionAmount] = useState('0');
  const [maxOrderAmount, setMaxOrderAmount] = useState('');
  const [maxDiscountAmount, setMaxDiscountAmount] = useState('');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [maxQuantity, setMaxQuantity] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [applicableProductIds, setApplicableProductIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code.trim() || !badge.trim() || !title.trim()) {
      setError('쿠폰코드, 스티커, 제목은 필수입니다.');
      return;
    }
    if (couponType === 'PRODUCT' && applicableProductIds.length === 0) {
      setError('상품 쿠폰은 적용 범위 상품을 1개 이상 선택해야 합니다.');
      return;
    }
    // [coupon-audit] BE 는 @PositiveOrZero 만 검증 — 150% 쿠폰이 그대로 저장된다. FE 가드.
    if (discountType === 'PERCENT' && (Number(discountAmount) <= 0 || Number(discountAmount) > 100)) {
      setError('정률 할인은 1~100% 사이여야 합니다.');
      return;
    }
    const body: CouponCreateRequest = {
      code: code.trim(),
      badge: badge.trim(),
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      couponType,
      discountType,
      discountAmount: Number(discountAmount) || 0,
      conditionAmount: Number(conditionAmount) || 0,
      ...(maxOrderAmount ? { maxOrderAmount: Number(maxOrderAmount) } : {}),
      ...(maxDiscountAmount ? { maxDiscountAmount: Number(maxDiscountAmount) } : {}),
      ...(budgetLimit ? { budgetLimit: Number(budgetLimit) } : {}),
      ...(maxQuantity ? { maxQuantity: Number(maxQuantity) } : {}),
      ...(validFrom ? { validFrom: new Date(validFrom).toISOString() } : {}),
      ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
      ...(couponType === 'PRODUCT' && applicableProductIds.length > 0
        ? { applicableProductIds }
        : {}),
    };
    mutation.mutate(body, {
      onSuccess: () => {
        onSuccess?.();
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6"
      data-testid="coupon-create-form"
    >
      <SectionCard title="쿠폰 등록">
        <div className="space-y-6">
          <FormSection label="쿠폰 종류">
            <CouponTypeRadio value={couponType} onChange={setCouponType} />
          </FormSection>

          <FormSection label="기본 정보">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-code" required>쿠폰코드</Label>
                  <Input
                    id="coupon-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="WELCOME2026"
                    maxLength={100}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-badge" required>스티커</Label>
                  <Input
                    id="coupon-badge"
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                    placeholder="신규가입"
                    maxLength={50}
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-title" required>제목</Label>
                <Input
                  id="coupon-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="신규 가입 쿠폰"
                  maxLength={255}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-desc">설명</Label>
                <Input
                  id="coupon-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="(선택)"
                  maxLength={1000}
                />
              </div>
            </div>
          </FormSection>

          <FormSection label="할인 조건">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label id="coupon-discount-type-label">할인 유형</Label>
                  <RadioGroup
                    value={discountType}
                    onValueChange={(v) => setDiscountType(v as CouponDiscountType)}
                    options={DISCOUNT_TYPE_OPTIONS}
                    orientation="horizontal"
                    aria-labelledby="coupon-discount-type-label"
                    className="min-h-8 items-center"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-discount-amount" required>할인 금액/율</Label>
                  <Input
                    id="coupon-discount-amount"
                    type="number"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="3000"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-condition">최소 주문금액</Label>
                  <Input
                    id="coupon-condition"
                    type="number"
                    min="0"
                    value={conditionAmount}
                    onChange={(e) => setConditionAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-max-order">최대 주문금액</Label>
                  <Input
                    id="coupon-max-order"
                    type="number"
                    min="0"
                    value={maxOrderAmount}
                    onChange={(e) => setMaxOrderAmount(e.target.value)}
                    placeholder="무제한"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-max-discount">최대 할인금액</Label>
                  <Input
                    id="coupon-max-discount"
                    type="number"
                    min="0"
                    value={maxDiscountAmount}
                    onChange={(e) => setMaxDiscountAmount(e.target.value)}
                    placeholder="정률 할인 cap (무제한)"
                  />
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection label="쿠폰 예산">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="coupon-budget-limit">예산 한도</Label>
                <Input
                  id="coupon-budget-limit"
                  type="number"
                  min="0"
                  value={budgetLimit}
                  onChange={(e) => setBudgetLimit(e.target.value)}
                  placeholder="0 또는 미입력 시 무제한"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-budget-spent">예산 사용액</Label>
                <Input
                  id="coupon-budget-spent"
                  type="text"
                  value="0"
                  readOnly
                  disabled
                  aria-label="예산 사용액 (자동 누적)"
                />
              </div>
            </div>
          </FormSection>

          <FormSection label="발급 설정">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="coupon-max-qty">최대 발급 수</Label>
                <Input
                  id="coupon-max-qty"
                  type="number"
                  min="1"
                  value={maxQuantity}
                  onChange={(e) => setMaxQuantity(e.target.value)}
                  placeholder="무제한"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-valid-from">유효 시작일</Label>
                <Input
                  id="coupon-valid-from"
                  type="datetime-local"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-valid-to">유효 종료일</Label>
                <Input
                  id="coupon-valid-to"
                  type="datetime-local"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                />
              </div>
            </div>
          </FormSection>

          {couponType === 'PRODUCT' ? (
            <FormSection label="적용 범위" required>
              <ProductMultiSelectModalField
                value={applicableProductIds}
                onChange={setApplicableProductIds}
                emptyHint="적용할 상품을 선택하세요 (상품 쿠폰 필수)"
              />
            </FormSection>
          ) : null}

          {error && (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {error}
            </p>
          )}

          <div className="flex justify-end border-t pt-4" style={{ borderColor: 'var(--divider-soft)' }}>
            <Button type="submit" variant="cta" size="md" disabled={mutation.isPending}>
              {mutation.isPending ? '등록 중...' : '쿠폰 등록'}
            </Button>
          </div>
        </div>
      </SectionCard>
    </form>
  );
};
